/**
 * Cliente del almacen en la nube.
 *
 * La aplicacion funciona en dos modos y el mismo codigo sirve para los dos:
 *
 *   local  -- en el escritorio, o abierta como pagina suelta. Los archivos se
 *             abren y se guardan del disco. No hay cuentas ni red.
 *   nube   -- servida desde el sitio web, con el arbol guardado en un
 *             repositorio privado. Se entra con una clave y se puede editar
 *             desde cualquier sitio.
 *
 * Cual de los dos se usa no se configura: se averigua al arrancar
 * preguntandole al servidor. Asi el mismo build vale para los dos.
 *
 * La ficha de sesion se guarda en el navegador. No es el token de GitHub
 * -- ese no sale nunca del servidor -- sino una ficha firmada que solo dice
 * "esta sesion puede editar" o "solo mirar", y que caduca.
 */

export type Rol = "editor" | "lector";

export interface EstadoNube {
  configurado: boolean;
  /** Si hay clave de solo lectura para compartir con la familia. */
  lectura: boolean;
}

export interface ArbolRemoto {
  /** null la primera vez, cuando todavia no se ha guardado nada. */
  texto: string | null;
  sha: string | null;
}

const LLAVE_FICHA = "arbol-familiar:ficha";
const LLAVE_ROL = "arbol-familiar:rol";

export class ErrorNube extends Error {
  constructor(
    readonly estado: number,
    mensaje: string,
  ) {
    super(mensaje);
  }

  /** Si el arbol cambio por debajo y hay que recargar antes de guardar. */
  get esConflicto(): boolean {
    return this.estado === 409;
  }

  /** Si la sesion caduco o no vale. */
  get esSesion(): boolean {
    return this.estado === 401;
  }
}

/** Lee algo del almacen del navegador sin romperse si esta bloqueado. */
function guardado(llave: string): string | undefined {
  try {
    return localStorage.getItem(llave) ?? undefined;
  } catch {
    return undefined;
  }
}

function guardar(llave: string, valor: string | undefined): void {
  try {
    if (valor === undefined) localStorage.removeItem(llave);
    else localStorage.setItem(llave, valor);
  } catch {
    // Navegacion privada o almacenamiento bloqueado: la sesion durara lo que
    // dure la pestana, que es molesto pero no impide trabajar.
  }
}

export class Nube {
  private ficha = guardado(LLAVE_FICHA);
  rol: Rol | undefined = guardado(LLAVE_ROL) as Rol | undefined;

  private constructor(readonly estado: EstadoNube) {}

  /**
   * Averigua si hay servidor detras. Devuelve undefined si no lo hay, que es
   * el caso del escritorio y el de abrir el fichero suelto.
   */
  static async detectar(): Promise<Nube | undefined> {
    try {
      const r = await fetch("/api/estado", { headers: { Accept: "application/json" } });
      if (!r.ok) return undefined;
      const j = (await r.json()) as { nube?: boolean; configurado?: boolean; lectura?: boolean };
      if (!j.nube) return undefined;
      return new Nube({ configurado: !!j.configurado, lectura: !!j.lectura });
    } catch {
      return undefined;
    }
  }

  get haySesion(): boolean {
    return !!this.ficha;
  }

  get puedeEditar(): boolean {
    return this.rol === "editor";
  }

  private async pedir(ruta: string, opciones: RequestInit = {}): Promise<unknown> {
    const cabeceras: Record<string, string> = {
      Accept: "application/json",
      ...((opciones.headers as Record<string, string>) ?? {}),
    };
    if (this.ficha) cabeceras["Authorization"] = `Bearer ${this.ficha}`;
    if (opciones.body) cabeceras["Content-Type"] = "application/json";

    const r = await fetch(ruta, { ...opciones, headers: cabeceras });
    const texto = await r.text();
    let datos: unknown = undefined;
    try {
      datos = texto ? JSON.parse(texto) : undefined;
    } catch {
      datos = undefined;
    }

    if (!r.ok) {
      const mensaje =
        (datos as { error?: string } | undefined)?.error ??
        `El servidor respondió ${r.status}.`;
      if (r.status === 401) this.olvidarSesion();
      throw new ErrorNube(r.status, mensaje);
    }
    return datos;
  }

  /** Cambia una clave por una sesion. */
  async entrar(clave: string): Promise<Rol> {
    const j = (await this.pedir("/api/sesion", {
      method: "POST",
      body: JSON.stringify({ clave }),
    })) as { ficha: string; rol: Rol };
    this.ficha = j.ficha;
    this.rol = j.rol;
    guardar(LLAVE_FICHA, j.ficha);
    guardar(LLAVE_ROL, j.rol);
    return j.rol;
  }

  salir(): void {
    this.olvidarSesion();
  }

  private olvidarSesion(): void {
    this.ficha = undefined;
    this.rol = undefined;
    guardar(LLAVE_FICHA, undefined);
    guardar(LLAVE_ROL, undefined);
  }

  async cargar(): Promise<ArbolRemoto> {
    const j = (await this.pedir("/api/arbol")) as {
      texto: string | null;
      sha: string | null;
      rol?: Rol;
    };
    // El servidor confirma el rol en cada lectura: si la clave de alguien
    // cambio de categoria, la interfaz se entera sin tener que salir y entrar.
    if (j.rol) {
      this.rol = j.rol;
      guardar(LLAVE_ROL, j.rol);
    }
    return { texto: j.texto, sha: j.sha };
  }

  /** Guarda y devuelve el sha nuevo. Lanza ErrorNube con 409 si hubo conflicto. */
  async guardar(texto: string, sha: string | null, nota?: string): Promise<string> {
    const j = (await this.pedir("/api/arbol", {
      method: "PUT",
      body: JSON.stringify({ texto, sha, nota }),
    })) as { sha: string };
    return j.sha;
  }
}
